import { differenceInMinutes } from 'date-fns';
import { NotificationType } from '../controllers/messaging';
import StatefulSettings, { canRequireProof } from '../controllers/StatefulSettings';
import { ScheduleItemProps } from '../itemViewers/ScheduleItem';
import {
    BaseTimedBasketItem, IEndEventTriggered, ScheduleBasketItem, IOptionSupport, IEventItem, IPercentComplete,
    ITaskItem, IPenaltyItem, TaskBasketItem, PenaltyBasketItem, ICountSupport, IVotableItem,
    ISettingsItem,
    PositionBasketItem
} from '../ts/Definitions';
import { ItemTypes, TaskType, HygieneBreak, PenaltyType, CountType } from '../ts/enums';
import Emitter, { ShowChangeScheduledTask, ShowRequestForItemDialog } from './Emitter';
import { isClothesCategory, isOutfitCategory, isTimedItem } from './utils';
import { convertToEventDayFromDate, formatValidDate } from './dateUtils';

export interface ITaskFlags {
    awaitTaskComplete: boolean, // this needs to work out if unique
    awaitingEventTask: boolean,
    allowPlayScheduleItem: boolean,
    showSpin: boolean | undefined,
    isAwaitingTimeTrigger: boolean | undefined,
    isChastity: boolean,
    timed: boolean | undefined,
    settingsItem: ISettingsItem | undefined,
    taskItem: ITaskItem | false,
    showOkButton: boolean,
    showDoneButton: boolean,
    showStartButton: boolean | undefined,
    showViewVoteButton: boolean | undefined,
    showReportFailedButton: boolean | undefined,
    showTimerIcon: boolean | undefined,
    showScheduleReasonIcon: boolean,
    showItemRequestIcon: boolean,
    showCompletedIcon: boolean,
    customScheduledEvent: boolean,
    showChangeButton: boolean | undefined,
    showReleaseButton: boolean | undefined,
    showProofButton: boolean | undefined,
    showPostponeButton: boolean | undefined,
    showRemoveButton: boolean | undefined,
    showWebLinkButton: boolean,
    showPermanentMessage: boolean | undefined,
    showEndEventTriggerName: boolean,
    showRemainingPercent: boolean | undefined,
    showRemainingMinutes: boolean | undefined,
    hasHygiene: boolean,
    percentRemaining: number,
    penaltiesForMissing: boolean,
    showPercentComplete: boolean,
    showTaskIcon: boolean,
    showOutfitIcon: boolean | undefined,
    showClothesIcon: boolean | undefined,
    showPenaltyIcon: boolean,
    showPermissionIcon: boolean,
    showEventIcon: boolean,
    showMetronomeIcon: boolean,
    showSiteEffectsIcon: boolean,
    showPositionIcon: boolean,
    showTaskImage: boolean,
    showTimerSetValue: boolean | undefined,
    showPendingItemTimeInfo: boolean | undefined,
    showPenaltyForMissingMessage: boolean,
    showTimeDueInfo: boolean,
    showDescriptionIcon: boolean | undefined,
    showHygieneInfo: boolean,
    showUsingSubTasks: boolean | undefined,
    imageUpdatedCallback: (newVal: string) => void,
    pendingDueTimeInfo: string,
    penaltyForMissingInfo: string,
    openInBrowser: () => void,
    start: () => void,
    spin: () => void,
    done: () => void,
    ok: () => void,
    remove: () => void,
    postpone: () => void,
    proof: () => void,
    release: () => void,
    hygiene: () => void,
    change: () => void,
    viewVote: () => void,
    failed: () => void,
    requestInfo: () => void,
    showInfo: () => void,
    showDescription: () => void,
    canShowDescription: boolean | undefined,
    getSubTasks: () => ITaskItem[],
    inProgress: boolean,
    showContinueButton: boolean,
    showDecrementCountButton: boolean,
    decrementCount: () => void,
    canCloseWindow: boolean;
    showBeginTimerButton: boolean;
    triggerEventDef: boolean;
    isPending: boolean;
    startText: string;
    showAwaitingRequestedByMessage: boolean;
    awaitingRequestedByMessage: string;
}

export const isMetronomePositionItem = (item: ISettingsItem | BaseTimedBasketItem | PositionBasketItem | undefined): boolean => {
    return item?.itemType === ItemTypes.position && (item as PositionBasketItem)?.hasMetronome === true;
};

export const isSiteEffectsPositionItem = (item: ISettingsItem | BaseTimedBasketItem | PositionBasketItem | undefined): boolean => {
    return item?.itemType === ItemTypes.position && (item as PositionBasketItem)?.hasSiteEffects === true;
};

export const examineTask = (settings: StatefulSettings, props: ScheduleItemProps): ITaskFlags => {
    const { item, failed, showRemainingTime, readonly, previewBasket,
        isOwnerView, remove, requestProof, isOwned, hideDescriptions, hideImages, hideSubTasks, play,
        releaseEvent, changePermission, changeTask, postpone, viewVote } = props;
    let inProgress = false;
    const timed = (item instanceof BaseTimedBasketItem)
        && (isTimedItem((item as BaseTimedBasketItem).countType)
            || (item as IEndEventTriggered).endTriggerEvent !== undefined);
    if (timed) {
        const timedItem = item as BaseTimedBasketItem;
        // NOTE: Do not use remainingMinutes here: some implementations return
        // the string "Complete" when the timer is finished, which is truthy and
        // can keep monitored dialogs locked open after countdown completion.
        const hasTicksRemaining = typeof timedItem.timerTicks === 'number'
            ? timedItem.timerTicks > 0
            : false;
        const hasEndTimeRemaining = (timedItem as { endTime?: Date }).endTime
            ? new Date((timedItem as { endTime?: Date }).endTime as Date) > new Date()
            : false;
        const isPermanentRunning = (timedItem as TaskBasketItem).permanent === true;
        inProgress = (hasTicksRemaining || hasEndTimeRemaining || isPermanentRunning)
            && !item.isPaused
            && !item.isComplete;
    }
    const countItem = (item as any as ICountSupport);
    const isAwaitingTimeTrigger = (item as ScheduleBasketItem).startTime && !inProgress;
    const settingsItem = settings.getCurrentItem(item);
    const isPending = (item as any).startTime !== undefined;
    const optionSupportItem = (settingsItem as IOptionSupport);
    let penaltiesForMissing = false;
    if (settingsItem && item.itemType === ItemTypes.event) {
        const eventItem = (settingsItem as IEventItem);
        penaltiesForMissing = ((eventItem.missEventPenalties !== undefined
            && eventItem.missEventPenalties.length > 0) ||
            (eventItem.missEventPillories !== undefined
                && eventItem.missEventPillories.length > 0));
    } else if (item.itemType === ItemTypes.event) { // this is an event we can't find, it's a schedule event
        const eventItem = (item as ScheduleBasketItem);
        penaltiesForMissing = ((eventItem.missEventPenalties !== undefined
            && eventItem.missEventPenalties.length > 0) ||
            (eventItem.missEventPillories !== undefined
                && eventItem.missEventPillories.length > 0));
    }
    const taskBasedItem = (item instanceof TaskBasketItem || item instanceof PenaltyBasketItem);
    const isTaskItem = item.itemType === ItemTypes.task;
    const taskItem = isTaskItem && item as any as ITaskItem;
    const isChastity = isTaskItem && taskItem && taskItem.creditTaskType === TaskType.chastity;
    const hasHygiene = isChastity && settings.getGeneralSettings().hygieneBreaks !== HygieneBreak.none && settings.isHygieneAllowed();
    const isPenaltyItem = item.itemType === ItemTypes.penalty;
    const penaltyItem = isPenaltyItem && item as any as IPenaltyItem;
    const isTaskedItem = isTaskItem || isPenaltyItem;
    const isLines = (taskItem && taskItem.creditTaskType === TaskType.lines) || (penaltyItem && penaltyItem.penaltyType === PenaltyType.lines);
    const isAcknowledgement = (taskItem && taskItem.creditTaskType === TaskType.acknowledgement)
        || (penaltyItem && penaltyItem.penaltyType === PenaltyType.acknowledgement)
        || item.itemType === ItemTypes.permission
        || (item.itemType === ItemTypes.event && !(item as TaskBasketItem).expectedCount);
    const isRouletteTask = taskItem && taskItem.creditTaskType === TaskType.roulette;
    const eventTriggerItem = taskBasedItem && item as any as IEndEventTriggered;
    const triggerEventDef = eventTriggerItem && eventTriggerItem.endTriggerEvent !== undefined && settings.getEvent(eventTriggerItem.endTriggerEvent);
    let percentRemaining = item.isComplete ? 0 : (item as unknown as IPercentComplete).percentComplete * 100;
    if (item instanceof PositionBasketItem) percentRemaining = item.percentComplete * 100;
    let awaitingEventTask = false;
    if (!item.isComplete && triggerEventDef && eventTriggerItem.endTriggerEvent && timed) {
        percentRemaining = 100;
        if (inProgress) {
            const eventTrigger = settings.getEvent(eventTriggerItem.endTriggerEvent);
            awaitingEventTask = eventTrigger !== undefined;
            if (awaitingEventTask && inProgress && item.startedTime) {
                const scheduleTimeForEvent = settings.getScheduleInfo().find(s => s.key === eventTriggerItem.endTriggerEvent);
                if (!scheduleTimeForEvent || !scheduleTimeForEvent.startTime) {
                    // means it's not set for today. the event is enabled however so it's going to await the next time set.
                    // do we guess it's enabled for tomorrow? or do we set it to be not an event
                    awaitingEventTask = false;
                } else {
                    const totalTicks = differenceInMinutes(scheduleTimeForEvent.startTime, item.startedTime);
                    const ticksCompleted = differenceInMinutes(new Date(), item.startedTime);
                    percentRemaining = 100 - ((ticksCompleted / totalTicks) * 100);
                }
            }
        }
    }
    const hasOptions = optionSupportItem
        && optionSupportItem.options
        && optionSupportItem.options
            .filter(o => o).length > 0;
    const showSpin = !previewBasket && !isOwnerView
        && !isAwaitingTimeTrigger
        && (item.itemType === ItemTypes.task || item.itemType === ItemTypes.penalty)
        && item.isPaused
        && !isAcknowledgement
        && !(item as any).selectedCondition
        && (isRouletteTask || hasOptions);
    const timeNotValid = (item as TaskBasketItem).permanent || awaitingEventTask;
    const showTime = !previewBasket && !timeNotValid && (showRemainingTime || isOwnerView || (item as TaskBasketItem | PenaltyBasketItem).alwaysShowTime);
    const showRemainingMinutes = timed && showTime;
    const showRemainingPercent = !previewBasket && timed && !showTime && !isOwnerView;
    const showEndEventTriggerName = !previewBasket && taskItem && inProgress && !(item as TaskBasketItem).permanent && awaitingEventTask;
    const showPermanentMessage = !previewBasket && taskItem && (item as TaskBasketItem).permanent;
    const allowPlayScheduleItem = readonly !== true; // not viewing schedule as another member
    const showWebLinkButton = !previewBasket && optionSupportItem && optionSupportItem.webLinkOptions && (taskItem as any).selectedCondition;
    const isAdmin = allowPlayScheduleItem && isOwnerView;
    const isMember = allowPlayScheduleItem && !isOwnerView;
    const showRemoveButton = isAdmin && remove && !item.isComplete;
    const showPostponeButton = !previewBasket && isAdmin && item.isPaused && isTaskedItem && postpone !== undefined;
    const showProofButton = !previewBasket && isAdmin && requestProof && canRequireProof(item);
    const showReleaseButton = isAdmin && releaseEvent && isAwaitingTimeTrigger;
    const showChangeButton = isAdmin && ((changePermission && item.itemType === ItemTypes.permission && !item.isComplete)
        || (changeTask && (item.itemType === ItemTypes.task
            || item.itemType === ItemTypes.penalty) && item.isPaused
            && ((hasOptions) // means we are awaiting there spin
                || (countItem.countType !== CountType.none
                    && countItem.countType !== undefined))));
    let countRemaining = 0;
    if (countItem.expectedCount)
        countRemaining = countItem.expectedCount;
    // only real way to tell is to look on a case by case basis if the task really should be monitored.
    const awaitTaskComplete = (item instanceof PositionBasketItem)
        || (taskBasedItem
            && timed
            && !triggerEventDef
            && ((settingsItem as ITaskItem | IPenaltyItem)?.monitoredTimer === true
                || (item as TaskBasketItem | PenaltyBasketItem).monitoredTimer === true));
    const waitingForRequestedByStart = !previewBasket
        && isMember
        && item.isPaused
        && !item.isComplete
        && (item as TaskBasketItem | PenaltyBasketItem).autoStartOnRequestedByStart === true
        && !!(item as TaskBasketItem | PenaltyBasketItem).requestedByKey;
    const waitingParentName = (item as TaskBasketItem | PenaltyBasketItem).requestedByName
        ?? (item as TaskBasketItem | PenaltyBasketItem).requestedByKey
        ?? 'the parent event';
    const awaitingRequestedByMessage = `This task will automatically start when '${waitingParentName}' is started`;
    const showOkButton = !previewBasket && !isOwnerView && isAcknowledgement && !awaitTaskComplete && !inProgress && !item.isComplete && !isAwaitingTimeTrigger;
    const countStillToDo = !previewBasket && isMember && !showSpin && !isAwaitingTimeTrigger && item.isPaused && !timed && !isLines && !isAcknowledgement && !waitingForRequestedByStart && countRemaining > 0;
    const showDoneButton = !previewBasket && isMember && !showSpin && !isAwaitingTimeTrigger && item.isPaused && !timed && !isLines && !isAcknowledgement && !waitingForRequestedByStart && !countStillToDo;
    const allowTimedStartFlow = (timed || isLines) && (!isAcknowledgement || awaitTaskComplete);
    const showStartButton = !previewBasket && isMember && item.isPaused
        && !showSpin && !isAwaitingTimeTrigger && allowTimedStartFlow && !awaitTaskComplete && !waitingForRequestedByStart;
    const showBeginTimerButton = !previewBasket && isMember && item.isPaused
        && !showSpin && !isAwaitingTimeTrigger && allowTimedStartFlow && awaitTaskComplete && !waitingForRequestedByStart;
    const showViewVoteButton = !previewBasket && allowPlayScheduleItem && viewVote && inProgress && (item as IVotableItem).votable && !awaitingEventTask;
    // Suppress "Failed" while a position/monitored timer has expired but the scheduler hasn't
    // yet confirmed isComplete — prevents the Failed button from sticking on at 100% progress.
    const awaitingCompletion = awaitTaskComplete && !inProgress && !item.isComplete;
    const showReportFailedButton = !previewBasket && isMember && failed !== undefined && !item.isComplete && !awaitingCompletion;
    const showTimerIcon = !previewBasket && !isOwnerView && inProgress && timed;
    const showScheduleReasonIcon = !previewBasket && item.scheduleReason !== undefined;
    const showItemRequestIcon = !inProgress && !previewBasket && isOwned && !isOwnerView;
    const showCompletedIcon = !previewBasket && item.isComplete;
    const customScheduledEvent = item.itemType === ItemTypes.event
        && settings.getEvent(item.key) === undefined;
    const showUsingSubTasks = !hideSubTasks && taskBasedItem
        && item.selectedItems
        && Object.keys(item.selectedItems).length > 0;
    const canCloseWindow = !awaitTaskComplete || !inProgress;
    let startText = 'Begin';
    if (inProgress) {
        startText = 'Info';
    }
    else if (item.isComplete) {
        startText = 'View';
    }

    return {
        startText,
        isPending,
        showAwaitingRequestedByMessage: waitingForRequestedByStart,
        awaitingRequestedByMessage,
        triggerEventDef: triggerEventDef !== undefined && triggerEventDef !== false,
        showBeginTimerButton,
        canCloseWindow,
        awaitTaskComplete, // this needs to work out if unique
        awaitingEventTask,
        allowPlayScheduleItem,
        showSpin,
        isAwaitingTimeTrigger,
        isChastity,
        timed,
        settingsItem,
        taskItem,
        showOkButton,
        showDoneButton,
        showStartButton,
        showViewVoteButton,
        showReportFailedButton,
        showTimerIcon,
        showScheduleReasonIcon,
        showItemRequestIcon,
        showCompletedIcon,
        customScheduledEvent,
        showChangeButton,
        showReleaseButton,
        showProofButton,
        showPostponeButton,
        showRemoveButton,
        showWebLinkButton,
        showPermanentMessage,
        showEndEventTriggerName,
        showRemainingPercent,
        showRemainingMinutes,
        hasHygiene,
        percentRemaining,
        penaltiesForMissing,
        showPercentComplete: timed && !item.isComplete && !item.isPaused && percentRemaining > 0,
        showTaskIcon: taskItem && (!taskItem.categories || !taskItem.categories.find(c => isClothesCategory(c) || isOutfitCategory(c))),
        showOutfitIcon: taskItem && taskItem.categories && taskItem.categories.find(c => isOutfitCategory(c)) !== undefined,
        showClothesIcon: taskItem && taskItem.categories && taskItem.categories.every(c => !isOutfitCategory(c)) && taskItem.categories.find(c => isClothesCategory(c)) !== undefined,
        showPenaltyIcon: item.itemType === ItemTypes.penalty,
        showPermissionIcon: item.itemType === ItemTypes.permission,
        showEventIcon: item.itemType === ItemTypes.event,
        showMetronomeIcon: isMetronomePositionItem(item),
        showSiteEffectsIcon: isSiteEffectsPositionItem(item),
        showPositionIcon: item.itemType === ItemTypes.position && !isMetronomePositionItem(item) && !isSiteEffectsPositionItem(item),
        showTaskImage: !customScheduledEvent && !hideImages,
        showTimerSetValue: isOwnerView
            && timed
            && item.isPaused
            && !(item as TaskBasketItem).permanent
            && !awaitingEventTask
            && (item.itemType === ItemTypes.task || item.itemType === ItemTypes.penalty)
            && (item as BaseTimedBasketItem).expectedCount !== undefined
            && (item as BaseTimedBasketItem).expectedCount !== 0,
        showPendingItemTimeInfo:
            (item instanceof ScheduleBasketItem || item instanceof TaskBasketItem || item instanceof PenaltyBasketItem)
            && item.startTime
            && item.isPaused,
        showPenaltyForMissingMessage: !isOwnerView && penaltiesForMissing,
        showTimeDueInfo: ((!readonly && isOwnerView) || penaltiesForMissing || customScheduledEvent),
        showDescriptionIcon: item.description !== undefined && !item.isComplete && !isAwaitingTimeTrigger,
        showHygieneInfo: isChastity && hasHygiene,
        showUsingSubTasks,
        imageUpdatedCallback: (newVal: string) => {
            item.img = newVal;
            item.imgId = undefined;
            settings.updateBasketItemImage(item);
            if (settingsItem) {
                settingsItem.img = newVal;
                settingsItem.imgId = undefined;
                settings.updateSettingsItem(settingsItem, false, true);
            } else if (isMetronomePositionItem(item) || isSiteEffectsPositionItem(item)) {
                const now = new Date();
                const customSchedule = settings.getScheduledCustomSchedule(convertToEventDayFromDate(now.getDay()), now);
                if (customSchedule) {
                    const updatedSchedule = isMetronomePositionItem(item)
                        ? {
                            ...customSchedule,
                            metronomeImg: newVal,
                            metronomeImgId: undefined,
                        }
                        : {
                            ...customSchedule,
                            siteEffectsImg: newVal,
                            siteEffectsImgId: undefined,
                        };
                    settings.saveCustomSchedule(updatedSchedule);
                    settings.saveCustomScheduledDay(updatedSchedule, convertToEventDayFromDate(new Date().getDay()));
                }
            }
        },
        pendingDueTimeInfo: ` due ${formatValidDate((item as any).startTime as Date, 'HH:mm')}`,
        penaltyForMissingInfo: '\n(Warning: Penalty for missing)',
        openInBrowser: () => window.open((item as any).selectedCondition, '_blank'),
        start: () => play(item),
        spin: () => play(item),
        done: () => play(item),
        ok: () => play(item),
        remove: () => {
            if (remove) remove(item);
        },
        postpone: () => {
            if (postpone) postpone(item);
        },
        proof: () => {
            if (requestProof) requestProof(item as TaskBasketItem | PenaltyBasketItem);
        },
        release: () => {
            if (releaseEvent) releaseEvent(item);
        },
        hygiene: () => {
            settings.lastHygieneDate = new Date();
            const locks = settings.getMyChastityTaskLocks();
            locks.forEach((lock) => settings.sendPauseLockRequest(lock));
        },
        change: () => {
            if (changePermission && item.itemType === ItemTypes.permission) changePermission(item);
            else {
                const taskItem = item as TaskBasketItem | PenaltyBasketItem;
                const baseItem = settings.getCurrentItem(taskItem); // ensures everything is populated for the dialog
                const changeItem = { ...baseItem, ...taskItem, count: taskItem.expectedCount };
                if (changeTask) changeTask(changeItem);
                else {
                    Emitter.emit(ShowChangeScheduledTask, settings, changeItem);
                }
            }
        },
        viewVote: () => {
            if (viewVote) viewVote(item);
        },
        failed: () => {
            if (failed) failed(item);
        },
        requestInfo: () => Emitter.emit(ShowRequestForItemDialog, item),
        showInfo: () => Emitter.emitNotification({ message: item.scheduleReason, notificationType: NotificationType.General }),
        showDescription: () => Emitter.emitNotification({ message: item.description, notificationType: NotificationType.General }),
        canShowDescription: !hideDescriptions && item.description !== undefined && !item.isComplete,
        getSubTasks: (): ITaskItem[] => {
            if (!showUsingSubTasks || !(item instanceof TaskBasketItem || item instanceof PenaltyBasketItem) || !item.selectedItems) return [];
            return Object.keys(item.selectedItems).map((innerTaskKey) => {
                const innerTask = settings.getTask(innerTaskKey);
                if (!innerTask) return null;
                if (item.selectedItems) innerTask.selectedCondition = item.selectedItems[innerTaskKey];
                return innerTask;
            }).filter(t => t !== null) as ITaskItem[];
        },
        inProgress,
        showContinueButton: (inProgress && !awaitTaskComplete) || item.isComplete,
        showDecrementCountButton: countStillToDo,
        decrementCount: () => {
            (item as TaskBasketItem).decrementCountRequired();
        }
    };
}